import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AuthService } from '@core/services/auth.service';
import { ColumnObject } from '@shared/components/shared-grid';

interface SavedColumnState {
  field: string;
  visible: boolean;
}

interface SavedColumnView {
  v: 1;
  columns: SavedColumnState[];
}

@Injectable({
  providedIn: 'root',
})
export class ColumnViewPersistenceService {
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _authService = inject(AuthService);

  /**
   * Loads the saved column view for a given page key.
   * @returns The saved column states or null if none exists.
   */
  loadView(pageKey: string): SavedColumnState[] | null {
    const key = this._buildKey(pageKey);
    if (!key) return null;

    const saved = this._localStorageService.getItem<SavedColumnView>(key);
    if (!saved?.v || saved.v !== 1 || !Array.isArray(saved.columns)) {
      return null;
    }
    return saved.columns;
  }

  /**
   * Saves the current column configuration for a given page key.
   */
  saveView(pageKey: string, columns: ColumnObject[]): void {
    const key = this._buildKey(pageKey);
    if (!key) return;

    const state: SavedColumnView = {
      v: 1,
      columns: columns.map(col => ({
        field: col.field,
        visible: col.showColumn,
      })),
    };
    this._localStorageService.setItem(key, state);
  }

  /**
   * Removes the saved column view for a given page key.
   */
  clearView(pageKey: string): void {
    const key = this._buildKey(pageKey);
    if (!key) return;
    this._localStorageService.removeItem(key);
  }

  /**
   * Checks whether a saved view exists for a given page key.
   */
  hasSavedView(pageKey: string): boolean {
    return this.loadView(pageKey) !== null;
  }

  /**
   * Merges saved column state with the current default columns.
   * - Columns present in defaults but not in saved state keep their default visibility.
   * - Columns in saved state but not in defaults are ignored (removed columns).
   * - Ordering follows the saved state, with new columns appended at the end.
   */
  normalizeColumns(
    defaultColumns: ColumnObject[],
    savedState: SavedColumnState[]
  ): ColumnObject[] {
    const savedMap = new Map(savedState.map(s => [s.field, s.visible]));
    const orderedFields = savedState.map(s => s.field);

    // Separate into: columns in saved order + new columns not in saved state
    const orderedColumns: ColumnObject[] = [];
    const remainingColumns: ColumnObject[] = [];

    for (const col of defaultColumns) {
      if (savedMap.has(col.field)) {
        // Will be placed in saved order
      } else {
        remainingColumns.push({ ...col });
      }
    }

    // Build ordered list from saved state
    for (const field of orderedFields) {
      const defaultCol = defaultColumns.find(c => c.field === field);
      if (defaultCol) {
        orderedColumns.push({
          ...defaultCol,
          showColumn: savedMap.get(field) ?? defaultCol.showColumn,
        });
      }
      // If not found in defaults, skip (column was removed)
    }

    // Append new columns at the end
    return [...orderedColumns, ...remainingColumns];
  }

  /**
   * Determines if the current column configuration differs from the baseline.
   *
   * Visible columns are compared positionally — display order is user-meaningful.
   * Hidden columns are compared as a Set — their position in the array is an
   * artefact of how handleGridViewChange appends them and carries no display meaning.
   * Using a Set here eliminates the false-positive that occurred when Region was
   * unticked and therefore appended to a different array index than in the baseline.
   */
  isDirty(
    currentColumns: ColumnObject[],
    defaultColumns: ColumnObject[]
  ): boolean {
    const currentVisible = currentColumns
      .filter(c => c.showColumn)
      .map(c => c.field);
    const baselineVisible = defaultColumns
      .filter(c => c.showColumn)
      .map(c => c.field);

    if (currentVisible.length !== baselineVisible.length) return true;
    for (let i = 0; i < currentVisible.length; i++) {
      if (currentVisible[i] !== baselineVisible[i]) return true;
    }

    const currentHidden = new Set(
      currentColumns.filter(c => !c.showColumn).map(c => c.field)
    );
    const baselineHidden = new Set(
      defaultColumns.filter(c => !c.showColumn).map(c => c.field)
    );

    if (currentHidden.size !== baselineHidden.size) return true;
    for (const field of currentHidden) {
      if (!baselineHidden.has(field)) return true;
    }

    return false;
  }

  private _buildKey(pageKey: string): string | null {
    const userId = this._authService.currentUser()?.id;
    if (!userId) return null;
    return `z-grid-view:${userId}:${pageKey}`;
  }
}
